import { RouteReuseStrategy, ActivatedRouteSnapshot, DetachedRouteHandle } from '@angular/router';

/**
 * Angular's default RouteReuseStrategy only reuses a component instance when
 * the future and current route point at the exact same route config object
 * (`future.routeConfig === curr.routeConfig`). Several feature modules (most
 * notably portal-management) register separate route entries for a bare tab
 * path and a deep-linked "tab/:entityId" path that both resolve to the same
 * component, so as far as Angular's default strategy is concerned they are
 * different routes - navigating between them (e.g. the deep-linkable-dialog
 * pattern's router.navigate() call right after opening a dialog) destroys
 * and recreates the whole component tree instead of just updating params.
 *
 * That destroy+recreate was the real cause of "duplicate dialog" bugs across
 * every portal-management tab: the freshly-recreated component's ngOnChanges
 * fires on its own initial input binding and reopens a second dialog, while
 * the first dialog (a PrimeNG DynamicDialog, attached to a global overlay
 * independent of the component that opened it) is still open.
 *
 * This strategy reuses the route (keeps the component alive, delivers
 * ngOnChanges instead of destroy/recreate) whenever future and current
 * resolve to the same component, regardless of which route config entry
 * matched - the one thing Angular's default explicitly does not check.
 */
export class SameComponentRouteReuseStrategy extends RouteReuseStrategy {
  shouldDetach(_route: ActivatedRouteSnapshot): boolean {
    return false;
  }

  store(_route: ActivatedRouteSnapshot, _handle: DetachedRouteHandle): void {
    // Never detach/store - see shouldDetach.
  }

  shouldAttach(_route: ActivatedRouteSnapshot): boolean {
    return false;
  }

  retrieve(_route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    return null;
  }

  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    if (future.routeConfig === curr.routeConfig) {
      return true;
    }
    const futureComponent = future.routeConfig?.component;
    const currComponent = curr.routeConfig?.component;
    return !!futureComponent && futureComponent === currComponent;
  }
}
